import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertCircle, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

const ROLES = [
  { id: "caregiver", label: "Caregiver", desc: "Standard family account." },
  { id: "therapist", label: "Therapist", desc: "Multi-client dashboard, invites, sponsored passes." },
  { id: "admin",     label: "Admin",     desc: "Full moderation queue, audit log, role grants. Use sparingly." },
];

export default function AdminGrant() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("therapist");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  useSEO({ title: "Admin · Grant role", noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOk("");
    if (role === "admin" && !window.confirm(`Promote ${email} to ADMIN?\n\nThey'll be able to verify therapists, take down family stories, view the audit log, and grant roles to other users. This is a permanent record (audit logged).`)) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/grant-role", { email: email.trim(), role, reason: reason.trim() || undefined });
      setOk(`✅ ${data.email} is now ${data.role}.`);
      setEmail(""); setReason("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  if (user && user.role !== "admin") {
    return <div className="min-h-screen dotted-bg flex items-center justify-center px-5"><div className="brutal-card bg-white p-6 max-w-md w-full"><h1 className="display text-2xl font-extrabold mb-2">Admins only</h1><Link to="/profiles" className="pill-btn primary">Back</Link></div></div>;
  }

  return (
    <div className="min-h-screen dotted-bg" data-testid="admin-grant-page">
      <header className="px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3">
        <Link to="/profiles" className="pill-btn"><ArrowLeft size={16} /> Back</Link>
        <div className="display text-lg sm:text-xl font-extrabold flex items-center gap-2">
          <ShieldCheck size={20} /> Admin · grant role
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <AdminTabs current="grant" />

        <p className="text-[color:var(--ink-soft)] mb-5">
          Promote an existing user to therapist or admin. Every change is logged in the audit trail with your email and the reason. Demoting a user back to caregiver also logs.
        </p>

        {error && <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-4 flex items-start gap-2" data-testid="grant-error"><AlertCircle size={16} className="mt-0.5" /> {error}</div>}
        {ok && <div className="brutal-card bg-[#E5F4DD] text-[color:var(--ink)] p-3 mb-4 flex items-start gap-2" data-testid="grant-ok"><Check size={16} className="mt-0.5" /> {ok}</div>}

        <form onSubmit={submit} className="brutal-card bg-white p-5 space-y-4" data-testid="grant-form">
          <label className="block">
            <span className="display font-extrabold text-sm mb-1 block">User email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="brutal-input" placeholder="someone@example.com" data-testid="grant-email" />
          </label>

          <fieldset>
            <legend className="display font-extrabold text-sm mb-2">New role</legend>
            <div className="space-y-2">
              {ROLES.map((r) => (
                <label key={r.id} className={`brutal-card p-3 cursor-pointer flex items-start gap-3 ${role === r.id ? "bg-[color:var(--gold)]" : "bg-white"}`} data-testid={`grant-role-${r.id}`}>
                  <input type="radio" name="role" value={r.id} checked={role === r.id} onChange={() => setRole(r.id)} className="mt-1" />
                  <div>
                    <div className="display font-extrabold">{r.label}</div>
                    <div className="text-xs text-[color:var(--ink-soft)]">{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className="display font-extrabold text-sm mb-1 block">Reason (audited)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="brutal-input" rows={2} maxLength={500} placeholder="e.g. Verified therapist credentials by phone — also a co-admin to cover weekends." data-testid="grant-reason" />
          </label>

          <button type="submit" disabled={busy || !email.trim()} className="pill-btn primary" data-testid="grant-submit">
            {busy ? "Granting…" : "Grant role"}
          </button>
        </form>
      </main>
    </div>
  );
}
